import React, { useState } from "react";

import { AsyncSelect, TSelectOptionGroup } from "@amphore-dev/amphore-lib";
import { format } from "date-fns";
import { t } from "i18next";
import { NavLink } from "react-router-dom";
import { toast } from "react-toastify";

import SearchService from "@/services/SearchService";

import { computeSearchResults } from "@/utils/USearch";
import { cn } from "@/utils/cn";

import {
	VIEW_SEARCH_GROUP,
	PROJECT_SEARCH_GROUP,
	TASK_SEARCH_GROUP,
} from "@/constants/CSearch";

import {
	TViewSearchResultOptions,
	TFormatedSearchGroups,
	TProjectSearchResultOptions,
	TSearchParams,
	TSelectedSearchOption,
	TTaskSearchResultOptions,
} from "@/types";

const MIN_SEARCH_LENGTH = 1;

/** The `total` react-select's own GroupBase never had a place for — Select's
 * TSelectOptionGroup doesn't carry it either (it's specific to "how many
 * more are there beyond what's shown", not a generic grouping concern), so
 * it rides along on the same object read back in `renderGroupHeader`. */
type TSearchOptionGroup = TSelectOptionGroup<TSelectedSearchOption> & {
	total: number;
};

export interface ISearchBarProps {
	placeholder?: string;
	onSelect?: (value: TSelectedSearchOption) => void;
	/** Every group fetched for the current query, as returned by the search API — e.g. to render results elsewhere on the page instead of (or alongside) this dropdown. */
	onFetched?: (groups: TFormatedSearchGroups[]) => void;
	className?: string;
	searchParams?: Partial<TSearchParams>;
	minSearchLength?: number;
	mergeTasks?: boolean;
	/** Per-group "+N" and the global "see more results" footer link, both to `/search`. Defaults to true. */
	showMoreLink?: boolean;
	disabled?: boolean;
	isClearable?: boolean;
}

export const SearchBar: React.FC<ISearchBarProps> = ({
	onSelect = () => {},
	onFetched = () => {},
	placeholder = t("search.placeholder"),
	className = "",
	searchParams = {},
	minSearchLength = MIN_SEARCH_LENGTH,
	mergeTasks = false,
	showMoreLink = true,
	disabled = false,
	isClearable = true,
}) => {
	const [query, setQuery] = useState("");
	const [hasResults, setHasResults] = useState(false);

	const loadOptions = (value: string): Promise<TSearchOptionGroup[]> => {
		setQuery(value);

		if (value.trim().length < minSearchLength) {
			setHasResults(false);
			return Promise.resolve([]);
		}

		return SearchService.search({ keyword: value, ...searchParams })
			.then((resp) => {
				const groups = computeSearchResults(resp, mergeTasks);
				onFetched(groups);
				setHasResults(groups.some((g) => g.options.length > 0));
				return groups.map((group) => ({
					label: group.label ?? "",
					total: group.total ?? group.options.length,
					options: group.options.map((item) => ({
						value: item,
						label: item.label,
					})),
				}));
			})
			.catch(() => {
				toast.error("An error occurred while searching");
				setHasResults(false);
				return [];
			});
	};

	return (
		<AsyncSelect<TSelectedSearchOption>
			className={className}
			placeholder={placeholder}
			disabled={disabled}
			isClearable={isClearable}
			loadOptions={loadOptions}
			debounce={500}
			clearInputOnSelect
			picto="search"
			getOptionValue={(item) => `${item.group}-${item.value}`}
			noResultsMessage={
				!query
					? t("search.globalPH")
					: query.trim().length < minSearchLength
						? t("search.minChars", { count: minSearchLength })
						: t("search.noResults", { search: query })
			}
			loadingMessage={t("search.loading")}
			onChange={(value) => {
				if (value) onSelect(value as TSelectedSearchOption);
			}}
			renderOption={(option) => <SearchResultRow data={option.value} />}
			renderGroupHeader={(group) => {
				const { total, options } = group as TSearchOptionGroup;
				const remaining = total - options.length;
				return (
					<div className="flex items-center justify-between gap-2">
						<span>{group.label}</span>
						{showMoreLink && remaining > 0 && (
							<NavLink
								to={`/search?type=${group.label.toLowerCase()}&query=${query}`}
							>
								{t("search.andMore", { count: remaining })}
							</NavLink>
						)}
					</div>
				);
			}}
			footer={
				showMoreLink && hasResults ? (
					<NavLink
						to={`/search?query=${query}`}
						className="block p-2 text-center text-neutral-500 no-underline! hover:bg-neutral-100"
					>
						{t("search.seeMore", { search: query })}
					</NavLink>
				) : undefined
			}
		/>
	);
};

const SearchResultRow: React.FC<{ data: TSelectedSearchOption }> = ({
	data,
}) => {
	if (data.group === VIEW_SEARCH_GROUP) {
		return <CalendarOption data={data as TViewSearchResultOptions} />;
	}
	if (data.group === PROJECT_SEARCH_GROUP) {
		return <ProjectOption data={data as TProjectSearchResultOptions} />;
	}
	if (data.group === TASK_SEARCH_GROUP) {
		return <TaskOption data={data as TTaskSearchResultOptions} />;
	}
	return null;
};

export interface ICalendarOptionProps {
	data: TViewSearchResultOptions;
}

export const CalendarOption: React.FC<ICalendarOptionProps> = ({ data }) => (
	<div className="row flex-1 items-center gap-2">
		<div
			style={{ backgroundColor: data.color }}
			className="h-3 w-3 min-w-3 rounded-xs"
		/>
		<span className="flex-1">{data.label}</span>
	</div>
);

export interface IProjectOptionProps {
	data: TProjectSearchResultOptions;
}

export const ProjectOption: React.FC<IProjectOptionProps> = ({ data }) => (
	<div className="row flex-1 items-center gap-2">
		<div
			style={{ backgroundColor: data.color }}
			className="h-2 w-2 min-w-2 rounded-full"
		/>
		<span className="flex-1">{data.label}</span>
	</div>
);

export interface ITaskOptionProps {
	data: TTaskSearchResultOptions;
	labelClassName?: string;
}

export const TaskOption: React.FC<ITaskOptionProps> = ({
	data,
	labelClassName,
}) => {
	const startDate = format(new Date(data.startDate), "dd-MM-yyyy");
	const endDate = data.endDate
		? format(new Date(data.endDate), "dd-MM-yyyy")
		: startDate;
	return (
		<>
			<div className="flex-1 gap-4 text-xs">
				<span className="line-clamp-2 flex-1 leading-4">
					{data.label}
				</span>
				<span className="text-neutral-400">
					{startDate +
						(data.endDate && startDate !== endDate
							? ` - ${endDate}`
							: "")}
				</span>
			</div>
			{data.project && (
				<span
					className={cn([
						"max-w-[60px] truncate rounded-md px-2 py-0 text-xs text-white",
						labelClassName,
					])}
					style={{ backgroundColor: data.project.color }}
				>
					{data.project.name}
				</span>
			)}
		</>
	);
};
